// Enchaînement de « Préparer la facture ». Les accès (CIME, Polaris, stockage) sont injectés
// pour tester l'ordre des appels sans réseau.
//   1. client du dossier (obligatoire pour les deux pièces) ;
//   2. frais → brouillon de facture CIME, lignes marquées refacturées ;
//   3. débours → fournisseurs trouvés ou créés, brouillon de facture de débours Polaris,
//      justificatifs déposés ligne par ligne, lignes marquées refacturées.
// Rien n'est finalisé : Polaris numérote seulement quand le cabinet finalise.
import {
  deboursToPolarLines,
  fraisToInvoiceLines,
  supplierKey,
  type PreparableExpense,
  type PreparationPlan,
} from './preparation';
import type { CimeContactParty } from '../../services/billing/polarClient';

export interface DisbursementPayload {
  id: number;
  contact_id: number;
  mandate_on: string | null;
  mandate_ref: string | null;
  lines: { supplier_id: number; number: string | null; piece_date: string; ttc_cents: number }[];
}

export interface PrepareDeps {
  findClient: (dossierId: string) => Promise<{ contactId: string; party: CimeContactParty } | null>;
  createFraisInvoice: (dossierId: string, clientContactId: string, lines: ReturnType<typeof fraisToInvoiceLines>) => Promise<string>;
  mandate: (dossierId: string) => Promise<{ on: string | null; ref: string | null }>;
  polarContactId: (party: CimeContactParty) => Promise<number>;
  findSupplier: (name: string) => Promise<number | null>;
  createSupplier: (name: string) => Promise<number>;
  createDisbursement: () => Promise<number>;
  saveDisbursement: (payload: DisbursementPayload) => Promise<void>;
  disbursementLineIds: (disbursementId: number) => Promise<number[]>;
  downloadReceipt: (path: string) => Promise<Blob>;
  uploadReceipt: (disbursementId: number, lineId: number, file: Blob, name: string) => Promise<void>;
  markBilled: (ids: string[], link: { billed_document_id?: string; polar_disbursement_id?: string }) => Promise<void>;
}

export interface PrepareResult {
  invoiceId: string | null;
  disbursementId: number | null;
  warnings: string[];
}

export async function prepareFromExpenses<T extends PreparableExpense>(
  plan: Extract<PreparationPlan<T>, { ok: true }>,
  deps: PrepareDeps,
): Promise<PrepareResult> {
  const client = await deps.findClient(plan.dossierId);
  if (!client) throw new Error('Ajoutez un client au dossier avant de préparer la facture.');
  const result: PrepareResult = { invoiceId: null, disbursementId: null, warnings: [] };

  if (plan.frais.length > 0) {
    result.invoiceId = await deps.createFraisInvoice(plan.dossierId, client.contactId, fraisToInvoiceLines(plan.frais));
    await deps.markBilled(plan.frais.map((e) => e.id), { billed_document_id: result.invoiceId });
  }

  if (plan.debours.length > 0) {
    const mandate = await deps.mandate(plan.dossierId);
    if (!mandate.on || !mandate.ref) {
      result.warnings.push('Mandat de débours manquant sur le dossier : Polaris refusera de finaliser.');
    }
    const contactId = await deps.polarContactId(client.party);

    const supplierIds = new Map<string, number>();
    for (const e of plan.debours) {
      const name = (e.supplier ?? '').trim();
      const key = supplierKey(name);
      if (supplierIds.has(key)) continue;
      supplierIds.set(key, (await deps.findSupplier(name)) ?? (await deps.createSupplier(name)));
    }

    const disbursementId = await deps.createDisbursement();
    await deps.saveDisbursement({
      id: disbursementId,
      contact_id: contactId,
      mandate_on: mandate.on,
      mandate_ref: mandate.ref,
      lines: deboursToPolarLines(plan.debours, supplierIds),
    });
    result.disbursementId = disbursementId;

    const lineIds = await deps.disbursementLineIds(disbursementId);
    for (const [i, e] of plan.debours.entries()) {
      const lineId = lineIds[i];
      if (!e.receipt_path || !lineId) continue;
      const file = await deps.downloadReceipt(e.receipt_path);
      await deps.uploadReceipt(disbursementId, lineId, file, e.receipt_path.split('/').pop() ?? 'justificatif.pdf');
    }
    if (plan.missingReceipts > 0) {
      result.warnings.push(
        `${plan.missingReceipts} débours sans justificatif : Polaris refusera de finaliser tant qu’il manque.`,
      );
    }
    await deps.markBilled(plan.debours.map((e) => e.id), { polar_disbursement_id: String(disbursementId) });
  }

  return result;
}
